<template>
  <div>
    <HeroHeader
      v-if="!embedded"
      eyebrow="ADMINISTRACIÓN"
      title="Gastos"
      :counter-num="list.length"
      :counter-unit="list.length === 1 ? 'gasto' : 'gastos'"
    >
      <template #toolbar>
        <button class="btn" @click="showNewSidebar = true">+ Agregar</button>
      </template>
    </HeroHeader>

    <!-- Embebido: el mes de la página y el CTA de esta pestaña comparten fila, así
         el selector no queda en una banda con la mitad derecha vacía.
         Suelto: filtro de fecha estándar del panel, el mismo que usa Ventas. -->
    <div v-if="period" class="filter-row">
      <MonthNav :model-value="period" @update:model-value="$emit('update:period', $event)"/>
      <div class="filter-row__actions">
        <button class="btn btn--sm" @click="showNewSidebar = true">+ Agregar gasto</button>
      </div>
    </div>
    <template v-else>
      <div v-if="embedded" class="embedded-toolbar">
        <button class="btn btn--sm" @click="showNewSidebar = true">+ Agregar gasto</button>
      </div>
      <DateFilter v-model="preset" @change="onDateFilterChange" />
    </template>

    <!-- Chips de estado -->
    <ChipsBar
      :chips="[{label:'Todo',value:'all'},{label:'Pendientes',value:'pending'},{label:'Pagados',value:'paid'}]"
      v-model="filterPaid"
    />

    <div class="filter-bar filter-bar--no-top">
      <SearchInput v-model="search" placeholder="Buscar por tipo…" label="Buscar gasto por tipo"/>
      <select v-if="!forcedEstate" v-model="filterEstate">
        <option value="All">Todas las propiedades</option>
        <option v-for="e in estates" :key="e.id" :value="e.id">{{ e.description }}</option>
      </select>
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div class="dt-wrapper">
      <table class="dt" v-if="filteredList.length > 0">
        <thead>
          <tr>
            <th v-if="!embedded">Local</th>
            <th>Tipo</th>
            <th>Monto</th>
            <th>Vencimiento</th>
            <th>Estado</th>
            <th aria-label="Acciones"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in pagedList" :key="o.id">
            <td v-if="!embedded">{{ o.local ? o.local.description : '—' }}</td>
            <td>{{ o.type }}</td>
            <td><span class="dt-cell__primary">{{ formatAmount(o.total, o.currency) }}</span></td>
            <td>{{ o.deadline ? formatDate(o.deadline) : '—' }}</td>
            <td>
              <span class="badge" :class="o.paid ? 'badge--success' : 'badge--error'">
                {{ o.paid ? 'Pagado' : 'Pendiente' }}
              </span>
            </td>
            <td>
              <div class="dt-actions">
                <span class="dt-icon" title="Editar" @click="openEdit(o)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </span>
                <span class="dt-icon" title="Registrar pago"
                      @click="showPaySidebar = true; sidebarData = o">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </span>
                <span class="dt-icon dt-icon--danger" title="Eliminar" @click="confirmDelete(o)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                  </svg>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <EmptyState v-else title="Sin gastos" message="No hay gastos para mostrar con los filtros actuales."/>
    </div>

    <div v-if="filteredList.length > pageSize" class="dt-pagination">
      <span class="dt-pagination__info">{{ pageFrom }}–{{ pageTo }} de {{ filteredList.length }}</span>
      <div class="dt-pagination__controls">
        <button :disabled="page <= 1" @click="page--">← Anterior</button>
        <button :disabled="page >= totalPages" @click="page++">Siguiente →</button>
      </div>
    </div>

    <NewExpense :estates="expenseEstates" :default-estate="forcedEstate" @close="showNewSidebar = false" @add="addSidebar"
                :class="{'show': showNewSidebar}" title="Nuevo gasto" :show-navbar="showNewSidebar"/>
    <UpdateExpense :estates="estates" :data="sidebarData" @close="showUpdateSidebar = false" @update="updateSidebar"
                   :class="{'show': showUpdateSidebar}" title="Editar gasto" :show-navbar="showUpdateSidebar"/>
    <Pay :data="sidebarData" @close="showPaySidebar = false" @update="updateSidebar"
         :class="{'show': showPaySidebar}" :show-navbar="showPaySidebar"/>
    <ConfirmDialog
      :show="showDeleteDialog"
      title="Eliminar gasto"
      :message="deleteMsg"
      confirm-text="Eliminar"
      cancel-text="Cancelar"
      variant="danger"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { db, Timestamp } from '@/firebase/firebaseConfig'
import NewExpense from './NewExpense'
import UpdateExpense from './UpdateExpense'
import Pay from './Pay'
import ConfirmDialog from '@/components/ConfirmDialog'
import HeroHeader from '@/components/HeroHeader'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import DateFilter from '@/components/DateFilter'
import MonthNav from '@/components/MonthNav'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import { periodRange } from '@/utils/period'
import moment from 'moment'

export default {
  name: 'Expenses',
  components: { NewExpense, UpdateExpense, Pay, ConfirmDialog, HeroHeader, ChipsBar, EmptyState, SortSelect, DateFilter, MonthNav, SearchInput },
  props: {
    // Modo embebido (p.ej. dentro del detalle de una propiedad): oculta el hero propio
    embedded: { type: Boolean, default: false },
    // Fuerza el filtro a un solo local: { id, description }
    forcedEstate: { type: Object, default: null },
    // Período 'YYYY-MM' impuesto por la página contenedora. Si viene, manda él y
    // esta vista no dibuja su propio filtro de fecha.
    period: { type: String, default: null }
  },
  emits: ['update:period', 'changed'],
  data () {
    return {
      list: [],
      estates: [],
      search: '',
      filterEstate: 'All',
      filterPaid: 'all',
      sortBy: 'deadline_desc',
      sortOptions: [
        { value: 'deadline_desc', label: 'Vencimiento (reciente primero)', field: 'deadline', type: 'date', dir: 'desc' },
        { value: 'deadline_asc', label: 'Vencimiento (antiguo primero)', field: 'deadline', type: 'date', dir: 'asc' },
        { value: 'amount_desc', label: 'Monto (mayor a menor)', field: 'total', type: 'number', dir: 'desc' },
        { value: 'estate', label: 'Local (A–Z)', field: 'local.description', type: 'string', dir: 'asc' },
        { value: 'type', label: 'Tipo (A–Z)', field: 'type', type: 'string', dir: 'asc' }
      ],
      preset: 'month',
      // Generación de consulta, para descartar respuestas que llegan tarde
      fetchGen: 0,
      page: 1,
      pageSize: 20,
      showNewSidebar: false,
      showUpdateSidebar: false,
      showPaySidebar: false,
      sidebarData: {},
      showDeleteDialog: false,
      expenseToDelete: null,
      deleting: false
    }
  },
  computed: {
    // En modo embebido, el picker de "Nuevo gasto" se limita al local del detalle
    expenseEstates () {
      if (this.forcedEstate) return this.estates.filter(e => e.id === this.forcedEstate.id)
      return this.estates
    },
    // Local vigente: si la página fuerza uno, ése manda. Sale de la prop y no de
    // `filterEstate`, que se fija después de una consulta: mientras esa consulta
    // viaja, la tabla embebida llegaba a listar gastos de OTRAS propiedades —y sin
    // columna Local que lo delatara, porque va oculta en modo embebido.
    activeEstateId () { return this.forcedEstate ? this.forcedEstate.id : this.filterEstate },
    filteredList () {
      let list = this.list
      if (this.activeEstateId !== 'All') list = list.filter(o => o.local && o.local.id === this.activeEstateId)
      if (this.filterPaid === 'pending') list = list.filter(o => !o.paid)
      else if (this.filterPaid === 'paid') list = list.filter(o => o.paid)
      if (this.search.trim()) {
        const q = this.search.trim().toLowerCase()
        list = list.filter(o => (o.type || '').toLowerCase().includes(q))
      }
      return list
    },
    sortedList () {
      return sortItems(this.filteredList, this.sortOptions.find(o => o.value === this.sortBy))
    },
    totalPages () { return Math.ceil(this.filteredList.length / this.pageSize) },
    pageFrom () { return (this.page - 1) * this.pageSize + 1 },
    pageTo () { return Math.min(this.page * this.pageSize, this.filteredList.length) },
    pagedList () { return this.sortedList.slice((this.page - 1) * this.pageSize, this.page * this.pageSize) },
    // El diálogo nombra el gasto con los mismos datos de la fila: quien confirma
    // ve cuál está borrando, no un "¿seguro?" a ciegas.
    deleteMsg () {
      const o = this.expenseToDelete
      if (!o) return ''
      const detail = [
        o.type,
        this.formatAmount(o.total, o.currency),
        o.local ? o.local.description : null,
        o.deadline ? this.formatDate(o.deadline) : null
      ].filter(Boolean).join(' · ')
      const parts = ['Vas a eliminar: ' + detail + '.']
      if (o.paid) parts.push('Ya está pagado y descuenta de la utilidad del local.')
      const n = this.voucherCount(o)
      if (n) parts.push('Se pierden ' + n + (n === 1 ? ' comprobante adjunto.' : ' comprobantes adjuntos.'))
      parts.push('La acción no se puede deshacer.')
      return parts.join(' ')
    }
  },
  watch: {
    // Embebido: el mes de la página es la consulta. immediate para la carga inicial.
    period: {
      immediate: true,
      handler (p) {
        if (!p) return
        const { start, end } = periodRange(p)
        this.fetchExpenses(start, end)
      }
    },
    search () { this.page = 1 },
    filterEstate () { this.page = 1 },
    filterPaid () { this.page = 1 },
    sortBy () { this.page = 1 }
  },
  async created () { await this.loadInfo() },
  methods: {
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    formatAmount (total, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(total || 0)
    },
    voucherCount (o) {
      const all = [...(o.vouchers || []), ...(o.voucher ? [o.voucher] : [])].filter(Boolean)
      return new Set(all).size
    },
    addSidebar (obj) { this.list.unshift(obj); this.page = 1; this.$emit('changed') },
    updateSidebar (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
      this.$emit('changed')
    },
    openEdit (o) {
      this.sidebarData = o
      this.showUpdateSidebar = true
    },
    confirmDelete (o) {
      this.expenseToDelete = o
      this.showDeleteDialog = true
    },
    cancelDelete () {
      this.showDeleteDialog = false
      this.expenseToDelete = null
    },
    // Se borra el documento y se saca la fila de la lista en memoria: volver a
    // consultar la colección entera por un borrado sería una lectura de más.
    async doDelete () {
      const o = this.expenseToDelete
      if (!o) return
      try {
        this.deleting = true
        await db.collection('expenses').doc(o.id).delete()
        this.list = this.list.filter(e => e.id !== o.id)
        if (this.page > 1 && this.page > this.totalPages) this.page = this.totalPages || 1
        this.showDeleteDialog = false
        this.expenseToDelete = null
        this.$emit('changed')
        this.$toast.success('Gasto eliminado')
      } catch (e) {
        this.$toast.error('Error al eliminar el gasto')
      } finally {
        this.deleting = false
      }
    },
    // Recibe { preset, from, to } del <DateFilter>: 'Todo' (o sin rango) trae
    // todos los gastos; cualquier otro, el rango de fechas.
    onDateFilterChange ({ preset, from, to }) {
      if (preset === 'all' || (!from && !to)) return this.fetchExpenses(null, null)
      this.fetchExpenses(new Date(from + 'T00:00:00'), new Date(to + 'T23:59:59'))
    },
    // Única consulta de la vista. start/end en null = sin filtro de fecha.
    // El mes es una flecha y se clickea en ráfaga: sin el token de request, dos
    // consultas solapadas terminaban empujando sus filas al mismo array y la tabla
    // mostraba dos meses juntos bajo una sola etiqueta.
    async fetchExpenses (start, end) {
      const gen = ++this.fetchGen
      try {
        this.$store.dispatch('showProgress')
        const list = []
        let query = db.collection('expenses')
        if (start && end) {
          query = query
            .where('deadline', '>=', Timestamp.fromDate(start))
            .where('deadline', '<=', Timestamp.fromDate(end))
            .orderBy('deadline', 'asc')
        } else {
          query = query.orderBy('deadline', 'desc')
        }
        const snap = await query.get()
        snap.forEach(doc => {
          const obj = { id: doc.id, ...doc.data() }
          obj.deadline = doc.data().deadline ? doc.data().deadline.toDate() : null
          obj.paymentDate = doc.data().paymentDate ? doc.data().paymentDate.toDate() : null
          list.push(obj)
        })
        if (gen !== this.fetchGen) return
        this.list = list
        this.page = 1
      } catch (e) {
        this.$toast.error('Error al cargar gastos')
      } finally {
        // Una respuesta vieja no apaga la barra de la consulta que la reemplazó
        if (gen === this.fetchGen) this.$store.dispatch('hideProgress')
      }
    },
    // Sólo catálogo, y sin tocar la barra de progreso: corre en paralelo con
    // fetchExpenses y `isProgress` es un booleano, no un contador — si las dos la
    // prendieran, la primera en terminar apagaría la del otro. La dueña de la barra
    // es fetchExpenses, que es la que llena la tabla.
    async loadInfo () {
      try {
        const snap = await db.collection('estates').orderBy('createdAt').get()
        snap.forEach(doc => this.estates.push({ id: doc.id, ...doc.data() }))
      } catch (e) {
        this.$toast.error('Error al cargar datos')
      }
    }
  }
}
</script>

<style scoped>
/* Toolbar propio cuando el módulo va embebido en el detalle de una propiedad */
.embedded-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px 0;
}
</style>
